import React, { useState } from "react";

const API = "https://canada-billing.ameontech.com";

const PROVINCES = ["AB","BC","MB","NB","NL","NS","NT","NU","ON","PE","QC","SK","YT"];
const CLIENT_TYPES = ["Business", "Individual", "Government", "Non-Profit"];
const INDUSTRIES = [
  "Technology & IT", "Consulting & Professional Services", "Construction & Trades",
  "Healthcare & Medical", "Retail & E-Commerce", "Finance & Accounting",
  "Legal Services", "Real Estate", "Manufacturing", "Transportation & Logistics",
  "Education & Training", "Media & Marketing", "Food & Hospitality", "Other"
];

const inp = {
  width: "100%", padding: "10px 13px", borderRadius: 9,
  border: "1.5px solid #e8edf3", fontSize: 14, color: "#1a1a2e",
  outline: "none", boxSizing: "border-box", background: "#fafbfd",
  fontFamily: "'DM Sans', sans-serif", transition: "border .2s",
};
const focusInp = (e) => (e.target.style.borderColor = "#4f8ef7");
const blurInp  = (e) => (e.target.style.borderColor = "#e8edf3");

const Field = ({ label, sub, children, required }) => (
  <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
    <label style={{ fontSize: 12.5, fontWeight: 600, color: "#374151", display: "flex", gap: 4 }}>
      {label}
      {required && <span style={{ color: "#f97b6b" }}>*</span>}
    </label>
    {sub && <span style={{ fontSize: 11.5, color: "#9ca3af", marginTop: -2 }}>{sub}</span>}
    {children}
  </div>
);

const Row2 = ({ children }) => (
  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
    {children}
  </div>
);

const SectionHead = ({ icon, title }) => (
  <div style={{
    display: "flex", alignItems: "center", gap: 8,
    margin: "8px 0 4px", borderBottom: "1.5px solid #f0f4fa", paddingBottom: 8
  }}>
    <span style={{ fontSize: 15 }}>{icon}</span>
    <span style={{ fontWeight: 700, fontSize: 13, color: "#374151" }}>{title}</span>
  </div>
);

const EMPTY = {
  client_name: "", client_type: "Business", industry: "", status: "Active",
  client_since: new Date().toISOString().split("T")[0],
  email: "", mobile: "", gst_number: "", notes: "",
  address_line1: "", address_line2: "", city: "Toronto", province: "ON",
  postal_code: "", country: "Canada",
  billing_address_same_as_primary: true,
  billing_address_line1: "", billing_address_line2: "",
  billing_city: "", billing_province: "ON",
  billing_postal_code: "", billing_country: "Canada",
};

export default function ClientOnboarding({ onComplete, onSkip }) {
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [form, setForm] = useState({ ...EMPTY });

  const set = (key) => (e) => {
    setForm(p => ({ ...p, [key]: e.target.value }));
    setErrors(p => ({ ...p, [key]: "" }));
  };
  const setCheck = (key) => (e) =>
    setForm(p => ({ ...p, [key]: e.target.checked }));

  const validate = () => {
    const e = {};
    if (!form.client_name.trim()) e.client_name = "Client name is required";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API}/api/clients`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error("Save failed");
      onComplete?.();
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700;800&family=DM+Sans:wght@300;400;500;600&display=swap');
        *, *::before, *::after { box-sizing: border-box; margin:0; padding:0; }
        html, body { height:100%; background:#f0f4fa; overflow:hidden; }

        .co-wrap {
          height: 100vh;
          display: flex;
          background: #f0f4fa;
          font-family: 'DM Sans', sans-serif;
          overflow: hidden;
        }

        .co-left {
          width: 340px;
          height: 100vh;
          background: #13131f;
          display: flex;
          flex-direction: column;
          padding: 40px 36px;
          position: relative;
          overflow: hidden;
          flex-shrink: 0;
        }

        .co-left::before {
          content: '';
          position: absolute;
          width: 320px; height: 320px;
          border-radius: 50%;
          background: #4f8ef7;
          filter: blur(100px);
          opacity: .09;
          top: -100px; right: -80px;
          pointer-events: none;
        }

        .co-right {
          flex: 1;
          height: 100vh;
          overflow-y: auto;
          display: flex;
          align-items: flex-start;
          justify-content: center;
          padding: 40px 32px;
        }

        .co-card {
          background: #fff;
          border-radius: 20px;
          width: 100%;
          max-width: 580px;
          padding: 36px 38px 32px;
          box-shadow: 0 4px 32px rgba(0,0,0,.08);
          margin-bottom: 40px;
        }

        .co-btn-primary {
          display: inline-flex; align-items: center; gap: 6px;
          padding: 11px 26px; border-radius: 50px; font-size: 14px;
          font-weight: 600; cursor: pointer; border: none;
          background: linear-gradient(135deg,#4f8ef7,#6366f1);
          color: #fff; font-family: 'DM Sans',sans-serif; transition: opacity .15s;
        }
        .co-btn-primary:hover { opacity: .9; }
        .co-btn-primary:disabled { opacity: .5; cursor: not-allowed; }

        .co-btn-ghost {
          display: inline-flex; align-items: center;
          padding: 11px 22px; border-radius: 50px; font-size: 14px;
          font-weight: 600; cursor: pointer; background: transparent;
          border: 1.5px solid #e8edf3; color: #6b7280;
          font-family: 'DM Sans',sans-serif; transition: all .15s;
        }
        .co-btn-ghost:hover { border-color: #4f8ef7; color: #4f8ef7; }

        .co-skip {
          background: none; border: none; cursor: pointer;
          font-size: 12.5px; color: rgba(255,255,255,.3);
          font-family: 'DM Sans',sans-serif; margin-top: 8px; transition: color .15s;
        }
        .co-skip:hover { color: rgba(255,255,255,.6); }

        .co-left-sticky { position: sticky; top: 40px; }
      `}</style>

      <div className="co-wrap">

        {/* LEFT PANEL */}
        <div className="co-left">
          <div className="co-left-sticky">
            <div style={{ display:"flex", alignItems:"center", gap:9, marginBottom:48, position:"relative", zIndex:2 }}>
              <div style={{ width:34, height:34, borderRadius:9, background:"linear-gradient(135deg,#4f8ef7,#6366f1)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:16 }}>🍁</div>
              <div>
                <div style={{ fontFamily:"'Playfair Display',serif", fontWeight:700, fontSize:19, color:"#fff", lineHeight:1 }}>Invoice<span style={{ color:"#4f8ef7" }}>Cloud</span></div>
                <div style={{ fontSize:10, fontWeight:700, letterSpacing:"1.4px", color:"#4f8ef7", textTransform:"uppercase", marginTop:2 }}>Canada</div>
              </div>
            </div>

            <div style={{ position:"relative", zIndex:2 }}>
              <div style={{ fontSize:11, fontWeight:700, letterSpacing:"1.5px", textTransform:"uppercase", color:"#4f8ef7", marginBottom:10 }}>Almost there! 🎉</div>
              <h2 style={{ fontFamily:"'Playfair Display',serif", fontWeight:800, fontSize:26, color:"#fff", lineHeight:1.3, marginBottom:14 }}>
                Add your first<br />client
              </h2>
              <p style={{ fontSize:14, color:"rgba(255,255,255,.45)", lineHeight:1.7, marginBottom:36 }}>
                Add a client now so you can start sending invoices right away. You can always add more later.
              </p>
              {[
                { icon:"✅", label:"Business profile complete" },
                { icon:"👤", label:"Add your first client", active: true },
                { icon:"🧾", label:"Start sending invoices" },
              ].map((item, i) => (
                <div key={i} style={{ display:"flex", alignItems:"center", gap:12, marginBottom:14 }}>
                  <div style={{ width:32, height:32, borderRadius:8, background: item.active ? "rgba(79,142,247,.15)" : i === 0 ? "rgba(82,201,126,.15)" : "rgba(255,255,255,.05)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:15, border: item.active ? "1px solid rgba(79,142,247,.3)" : "none" }}>
                    {item.icon}
                  </div>
                  <span style={{ fontSize:13.5, fontWeight: item.active ? 600 : 400, color: item.active ? "#fff" : i === 0 ? "rgba(82,201,126,.8)" : "rgba(255,255,255,.35)" }}>
                    {item.label}
                  </span>
                </div>
              ))}
            </div>

            <div style={{ position:"relative", zIndex:2, marginTop:32 }}>
              <button className="co-skip" onClick={() => onSkip?.()}>
                Skip for now →
              </button>
            </div>
          </div>
        </div>

        {/* RIGHT PANEL - only this scrolls */}
        <div className="co-right">
          <div className="co-card">
            {/* Card Header */}
            <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:24 }}>
              <div style={{ width:40, height:40, borderRadius:10, background:"rgba(79,142,247,.1)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:20 }}>👤</div>
              <div>
                <div style={{ fontFamily:"'Playfair Display',serif", fontWeight:700, fontSize:18, color:"#1a1a2e" }}>Add your first client</div>
                <div style={{ fontSize:12.5, color:"#9ca3af", marginTop:2 }}>Fill in all client details below</div>
              </div>
            </div>

            <div style={{ display:"flex", flexDirection:"column", gap:12 }}>

              <SectionHead icon="👤" title="Basic Information" />

              <Field label="Client Name" required>
                <input
                  style={{ ...inp, borderColor: errors.client_name ? "#f97b6b" : "#e8edf3" }}
                  value={form.client_name} onChange={set("client_name")}
                  placeholder="Acme Corp" onFocus={focusInp} onBlur={blurInp}
                />
                {errors.client_name && <span style={{ fontSize:11.5, color:"#f97b6b" }}>{errors.client_name}</span>}
              </Field>

              <Row2>
                <Field label="Client Type">
                  <select style={inp} value={form.client_type} onChange={set("client_type")}>
                    {CLIENT_TYPES.map(t => <option key={t}>{t}</option>)}
                  </select>
                </Field>
                <Field label="Status">
                  <select style={inp} value={form.status} onChange={set("status")}>
                    <option>Active</option>
                    <option>Inactive</option>
                  </select>
                </Field>
              </Row2>

              <Row2>
                <Field label="Industry">
                  <select style={inp} value={form.industry} onChange={set("industry")}>
                    <option value="">-- Select --</option>
                    {INDUSTRIES.map(i => <option key={i}>{i}</option>)}
                  </select>
                </Field>
                <Field label="Client Since">
                  <input style={inp} type="date" value={form.client_since} onChange={set("client_since")} onFocus={focusInp} onBlur={blurInp} />
                </Field>
              </Row2>

              <SectionHead icon="📞" title="Contact" />

              <Row2>
                <Field label="Email" sub="For sending invoices">
                  <input style={inp} type="email" value={form.email} onChange={set("email")} placeholder="info@acme.ca" onFocus={focusInp} onBlur={blurInp} />
                </Field>
                <Field label="Mobile">
                  <input style={inp} value={form.mobile} onChange={set("mobile")} placeholder="416-555-0100" onFocus={focusInp} onBlur={blurInp} />
                </Field>
              </Row2>

              <Field label="GST/HST Number" sub="e.g. 123456789 RT0001">
                <input style={inp} value={form.gst_number} onChange={set("gst_number")} placeholder="123456789 RT0001" onFocus={focusInp} onBlur={blurInp} />
              </Field>

              <SectionHead icon="📍" title="Primary Address" />

              <Field label="Address Line 1">
                <input style={inp} value={form.address_line1} onChange={set("address_line1")} placeholder="123 King St W" onFocus={focusInp} onBlur={blurInp} />
              </Field>
              <Field label="Address Line 2">
                <input style={inp} value={form.address_line2} onChange={set("address_line2")} placeholder="Suite 200" onFocus={focusInp} onBlur={blurInp} />
              </Field>
              <Row2>
                <Field label="City">
                  <input style={inp} value={form.city} onChange={set("city")} placeholder="Toronto" onFocus={focusInp} onBlur={blurInp} />
                </Field>
                <Field label="Province">
                  <select style={inp} value={form.province} onChange={set("province")}>
                    <option value="">-- Select --</option>
                    {PROVINCES.map(p => <option key={p}>{p}</option>)}
                  </select>
                </Field>
              </Row2>
              <Row2>
                <Field label="Postal Code">
                  <input style={inp} value={form.postal_code} onChange={set("postal_code")} placeholder="M5V 1A1" onFocus={focusInp} onBlur={blurInp} />
                </Field>
                <Field label="Country">
                  <input style={inp} value={form.country} onChange={set("country")} placeholder="Canada" onFocus={focusInp} onBlur={blurInp} />
                </Field>
              </Row2>

              <SectionHead icon="🏦" title="Billing Address" />

              <label style={{ display:"flex", alignItems:"center", gap:8, cursor:"pointer", fontSize:14, color:"#374151" }}>
                <input
                  type="checkbox"
                  checked={form.billing_address_same_as_primary}
                  onChange={setCheck("billing_address_same_as_primary")}
                />
                Same as Primary Address
              </label>

              {!form.billing_address_same_as_primary && (
                <>
                  <Field label="Billing Address Line 1">
                    <input style={inp} value={form.billing_address_line1} onChange={set("billing_address_line1")} placeholder="456 Bay St" onFocus={focusInp} onBlur={blurInp} />
                  </Field>
                  <Field label="Billing Address Line 2">
                    <input style={inp} value={form.billing_address_line2} onChange={set("billing_address_line2")} placeholder="Floor 10" onFocus={focusInp} onBlur={blurInp} />
                  </Field>
                  <Row2>
                    <Field label="City">
                      <input style={inp} value={form.billing_city} onChange={set("billing_city")} placeholder="Toronto" onFocus={focusInp} onBlur={blurInp} />
                    </Field>
                    <Field label="Province">
                      <select style={inp} value={form.billing_province} onChange={set("billing_province")}>
                        <option value="">-- Select --</option>
                        {PROVINCES.map(p => <option key={p}>{p}</option>)}
                      </select>
                    </Field>
                  </Row2>
                  <Row2>
                    <Field label="Postal Code">
                      <input style={inp} value={form.billing_postal_code} onChange={set("billing_postal_code")} placeholder="M5H 2N2" onFocus={focusInp} onBlur={blurInp} />
                    </Field>
                    <Field label="Country">
                      <input style={inp} value={form.billing_country} onChange={set("billing_country")} placeholder="Canada" onFocus={focusInp} onBlur={blurInp} />
                    </Field>
                  </Row2>
                </>
              )}

              <SectionHead icon="📝" title="Notes" />

              <Field label="Notes">
                <textarea
                  style={{ ...inp, resize:"vertical" }}
                  rows={3}
                  value={form.notes}
                  onChange={set("notes")}
                  placeholder="Any additional notes..."
                  onFocus={focusInp} onBlur={blurInp}
                />
              </Field>

            </div>

            {/* Footer Buttons */}
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginTop:28 }}>
              <button className="co-btn-ghost" onClick={() => onSkip?.()}>Skip for now</button>
              <button className="co-btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? "Saving…" : "Add Client & Continue →"}
              </button>
            </div>
          </div>
        </div>

      </div>
    </>
  );
}